import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { Router, RouterModule } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { CasoUsoService } from '@services/casoUso/casoUso.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { SnackBarService } from '@services/snackbar/snack-bar-service.service';
import { CasoUsoDto } from '@models/seguridad/casosUso/casoUsoDto.model';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { ButtonFilterTableClearComponent } from '@component/button-filter-table-clear/button-filter-table-clear.component';
import { ButtonFilterTableComponent } from '@component/button-filter-table/button-filter-table.component';
import { PanelFiltrosAvanzadosComponent } from '@component/panel-filtros-avanzados/panel-filtros-avanzados.component';
import { ToggleFiltrosAvanzadosComponent } from '@component/toggle-filtros-avanzados/toggle-filtros-avanzados.component';

interface TreeNode {
  id: number;
  name: string;
  icon?: string;
  url?: string;
  children?: TreeNode[];
  casoUso?: CasoUsoDto;
  level?: number;
  parentId?: number;
}

interface TreeStats {
  totalNodes: number;
  visibleNodes: number;
  hiddenNodes: number;
  enabledNodes: number;
  disabledNodes: number;
  menuDesplegables: number;
}

@Component({
    imports: [ToggleFiltrosAvanzadosComponent, PanelFiltrosAvanzadosComponent, 
        CommonModule,
        FormsModule,
        RouterModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatTreeModule,
        NgxSpinnerModule,
        MatTooltipModule,
        MatDialogModule,
        MatChipsModule,
        MatMenuModule,
        MatSlideToggleModule,
        MatCheckboxModule,
        MatDividerModule,
        ButtonFilterTableClearComponent,
        ButtonFilterTableComponent
    ],
    selector: 'app-menues-desplegables',
    templateUrl: './menues-desplegables.component.html',
    styleUrls: ['./menues-desplegables.component.css'],
    animations: [
        trigger('expandCollapse', [
            state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
            state('expanded', style({ height: '*', opacity: 1 })),
            transition('collapsed <=> expanded', animate('300ms ease-in-out')),
        ]),
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ])
        ])
    ]
})
export class MenuesDesplegablesComponent implements OnInit {
  private casoUsoService = inject(CasoUsoService);
  private snackBar = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private breakpointObserver = inject(BreakpointObserver);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  // Propiedades del árbol
  filterText = '';
  treeControl = new NestedTreeControl<TreeNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<TreeNode>();
  originalTreeData!: TreeNode[];
  selectedNode: TreeNode | null = null;
  showDetails = false;
  allCasosUso: CasoUsoDto[] = [];

  // Debounce para filtro
  private filterSubject = new Subject<string>();

  // Estadísticas
  stats: TreeStats = {
    totalNodes: 0,
    visibleNodes: 0,
    hiddenNodes: 0,
    enabledNodes: 0,
    disabledNodes: 0,
    menuDesplegables: 0
  };

  // Controles de UI
  showStats = false;
  isCompactMode = false;
  showAdvancedFilters = false;

  // Filtros avanzados
  advancedFilters = {
    showVisible: true,
    showHidden: true,
    showEnabled: true,
    showDisabled: true,
    descripcion: '',
    observaciones: ''
  };

  // Debounce para filtros avanzados de texto (Descripción / Observaciones)
  private advancedFilterSubject = new Subject<void>();

  constructor() {
    this.setupFilterDebounce();
  }

  ngOnInit(): void {
    this.getAllCasoUso();
  }

  // Setup del debounce para filtro
  private setupFilterDebounce(): void {
    this.filterSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(filterValue => {
      this.performFilter(filterValue);
    });

    this.advancedFilterSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.applyAdvancedFilters();
    });
  }

  // Trigger del filtro
  filterTree(): void {
    this.filterSubject.next(this.filterText);
  }

  // Trigger del filtro avanzado por texto (Descripción / Observaciones)
  onAdvancedTextFilterChange(): void {
    this.advancedFilterSubject.next();
  }

  // Ejecutar filtro
  private performFilter(filter: string): void {
    const trimmedFilter = filter.trim().toLowerCase();

    if (!trimmedFilter) {
      this.applyAdvancedFilters();
      this.treeControl.collapseAll();
      return;
    }

    const filtered = this.originalTreeData
      .map(parent => {
        const filteredChildren = parent.children?.filter(child =>
          child.name.toLowerCase().includes(trimmedFilter) ||
          child.casoUso?.descripcion?.toLowerCase().includes(trimmedFilter) ||
          child.casoUso?.nombre?.toLowerCase().includes(trimmedFilter)
        ) || [];

        if (filteredChildren.length > 0 || parent.name.toLowerCase().includes(trimmedFilter)) {
          return {
            ...parent,
            children: filteredChildren.length > 0 ? filteredChildren : parent.children
          };
        }

        return null;
      })
      .filter(Boolean) as TreeNode[];

    this.dataSource.data = filtered;
    this.treeControl.dataNodes = filtered;
    this.treeControl.expandAll();
  }

  // Convertir casos de uso a estructura de árbol
  convertirCasosDeUsoAgrupados(casos: CasoUsoDto[]): TreeNode[] {
    const menuMap: Record<string, TreeNode> = {};
    const categoriaGenerica = 'Sin Categoría';

    for (const caso of casos) {
      const menuDesc = caso.menu?.descripcion || categoriaGenerica;

      if (!menuMap[menuDesc]) {
        menuMap[menuDesc] = {
          name: menuDesc,
          id: caso.menu?.id || 0,
          icon: caso.menu?.icon || 'folder',
          children: [],
          level: 0
        };
      }

      menuMap[menuDesc].children!.push({
        name: caso.descripcion,
        icon: caso.icon || 'description',
        url: caso.path,
        id: caso.id,
        casoUso: caso,
        level: 1,
        parentId: caso.menu?.id
      });
    }

    return Object.values(menuMap);
  }

  // Obtener todos los casos de uso
  getAllCasoUso(): void {
    this.spinner.show('spListado');
    this.casoUsoService.getAll()
      .subscribe({
        next: data => {
          this.spinner.hide('spListado');
          this.allCasosUso = data;

          const filtrados = data.filter(caso => caso.esMenuDesplegable);
          const treeData = this.convertirCasosDeUsoAgrupados(filtrados);
          this.originalTreeData = treeData;
          this.dataSource.data = treeData;

          this.calculateStats();
        },
        error: error => {
          this.spinner.hide('spListado');
          this.snackBar.showError(error, "Error");
        }
      });
  }

  // Calcular estadísticas
  calculateStats(): void {
    let totalNodes = 0;
    let visibleNodes = 0;
    let hiddenNodes = 0;
    let enabledNodes = 0;
    let disabledNodes = 0;
    let menuDesplegables = 0;

    this.originalTreeData.forEach(parent => {
      parent.children?.forEach(child => {
        if (child.casoUso) {
          totalNodes++;
          if (child.casoUso.visible) visibleNodes++;
          else hiddenNodes++;
          if (child.casoUso.habilitado) enabledNodes++;
          else disabledNodes++;
          if (child.casoUso.esMenuDesplegable) menuDesplegables++;
        }
      });
    });

    this.stats = {
      totalNodes,
      visibleNodes,
      hiddenNodes,
      enabledNodes,
      disabledNodes,
      menuDesplegables
    };
  }

  // Toggle filtros avanzados
  toggleAdvancedFilters(event: any): void {
    this.showAdvancedFilters = event.checked;
  }

  // Limpiar todos los filtros (texto general y avanzados)
  limpiarFiltro(): void {
    this.filterText = '';
    this.advancedFilters = {
      showVisible: true,
      showHidden: true,
      showEnabled: true,
      showDisabled: true,
      descripcion: '',
      observaciones: ''
    };
    this.applyAdvancedFilters();
    this.treeControl.collapseAll();
  }

  // Aplicar filtros avanzados
  applyAdvancedFilters(): void {
    const descripcionFilter = this.advancedFilters.descripcion.trim().toLowerCase();
    const observacionesFilter = this.advancedFilters.observaciones.trim().toLowerCase();

    const filtered = this.originalTreeData
      .map(parent => {
        const filteredChildren = parent.children?.filter(child => {
          if (!child.casoUso) return true;

          const matchesVisible =
            (this.advancedFilters.showVisible && child.casoUso.visible) ||
            (this.advancedFilters.showHidden && !child.casoUso.visible);

          const matchesEnabled =
            (this.advancedFilters.showEnabled && child.casoUso.habilitado) ||
            (this.advancedFilters.showDisabled && !child.casoUso.habilitado);

          const matchesDescripcion = !descripcionFilter ||
            child.casoUso.descripcion?.toLowerCase().includes(descripcionFilter);

          const matchesObservaciones = !observacionesFilter ||
            child.casoUso.observaciones?.toLowerCase().includes(observacionesFilter);

          return matchesVisible && matchesEnabled && matchesDescripcion && matchesObservaciones;
        }) || [];

        if (filteredChildren.length > 0) {
          return {
            ...parent,
            children: filteredChildren
          };
        }

        return null;
      })
      .filter(Boolean) as TreeNode[];

    this.dataSource.data = filtered;
    this.treeControl.dataNodes = filtered;
    if (descripcionFilter || observacionesFilter) {
      this.treeControl.expandAll();
    }
  }

  // Verificar si tiene hijos
  hasChild = (_: number, node: TreeNode) => !!node.children && node.children.length > 0;

  // Expandir todo
  expandAll(): void {
    this.treeControl.expandAll();
  }

  // Colapsar todo
  collapseAll(): void {
    this.treeControl.collapseAll();
  }

  // Mostrar detalles de nodo
  showNodeDetails(node: TreeNode): void {
    if (node.casoUso) {
      this.selectedNode = node;
      this.showDetails = true;
    }
  }

  // Cerrar panel de detalles
  closeDetails(): void {
    this.showDetails = false;
    this.selectedNode = null;
  }


  // Toggle estadísticas
  toggleStats(): void {
    this.showStats = !this.showStats;
  }

  // Toggle modo compacto
  toggleCompactMode(): void {
    this.isCompactMode = !this.isCompactMode;
  }

  // Exportar configuración
  exportConfiguration(): void {
    const config = {
      casosUso: this.allCasosUso.filter(c => c.esMenuDesplegable),
      treeStructure: this.originalTreeData,
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(config, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `menu-config-${new Date().getTime()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    this.snackBar.showSuccess('Configuración exportada correctamente', 'Éxito');
  }

  // Copiar ruta del nodo
  copyNodePath(node: TreeNode): void {
    const path = node.casoUso?.path || node.name;
    navigator.clipboard.writeText(path).then(() => {
      this.snackBar.showSuccess('Ruta copiada: ' + path, 'Éxito');
    }).catch(() => {
      this.snackBar.showError('Error al copiar ruta', 'Error');
    });
  }

  // Obtener clase del nodo según estado
  getNodeClass(node: TreeNode): string {
    if (!node.casoUso) return '';

    const classes = [];
    if (!node.casoUso.visible) classes.push('opacity-50');
    if (!node.casoUso.habilitado) classes.push('text-decoration-line-through text-muted');

    return classes.join(' ');
  }

  // Obtener URL completa del nodo
  getFullUrl(node: TreeNode): string {
    return node?.id ? `/menusdesplegables/${node.id}` : '/menusdesplegables';
  }

  // Click en nodo
  onNodeClick(node: TreeNode): void {
    const url = this.routeFullUrl(node);
    this.router.navigate([url]);
  }

  // Ruta completa
  routeFullUrl(node: TreeNode): string {
    return node?.id ? `/menusdesplegables/${node.id}` : '/menusdesplegables';
  }

  // Refrescar datos
  refresh(): void {
    this.getAllCasoUso();
    this.snackBar.showInfo('Datos actualizados', 'Información');
  }
}
